import { useEffect, useRef, useState } from "react";
import { ArrowUp, Square, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Composer: auto-growing textarea, Enter sends (Shift+Enter = newline), Stop while streaming.
 * `contextLabel` is shown as the active context (e.g. "Vik kraftverk · Siste 7 dager").
 */
export const Composer = ({ onSend, onStop, sending, placeholder = "Spør om hva som helst – om kraftverkene dine eller generelt …", contextLabel, onClearContext, autoFocus = false, initialValue = "", className, compact = false }) => {
  const [value, setValue] = useState(initialValue);
  const ref = useRef(null);

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, compact ? 160 : 240)}px`;
  }, [value, compact]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const submit = () => {
    const v = value.trim();
    if (!v || sending) return;
    onSend(v);
    setValue("");
  };

  return (
    <div
      className={cn("rounded-2xl border border-border bg-card shadow-[0_1px_2px_hsl(var(--brand-ink)/0.05),0_8px_24px_-12px_hsl(var(--brand-ink)/0.12)] transition-[border-color,box-shadow] duration-150 focus-within:border-primary/50 focus-within:shadow-[0_1px_2px_hsl(var(--brand-ink)/0.05),0_12px_32px_-12px_hsl(var(--brand-ink)/0.18),0_0_0_3px_hsl(var(--primary)/0.10)]", className)}
      onClick={() => ref.current?.focus()}
      data-testid="ai-composer"
    >
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
          }
        }}
        rows={1}
        placeholder={placeholder}
        className={cn("block w-full resize-none bg-transparent px-4 pt-3.5 text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none", compact ? "min-h-[44px] max-h-[160px]" : "min-h-[48px] max-h-[240px]")}
        aria-label="Skriv et spørsmål"
        data-testid="ai-composer-textarea"
      />
      <div className="flex items-center justify-between gap-3 px-2.5 pb-2.5 pt-1.5">
        <div className="flex min-w-0 items-center gap-2 pl-1.5">
          {contextLabel ? (
            <span className="inline-flex h-7 max-w-full items-center gap-1 rounded-md bg-primary/10 pl-2.5 pr-1 text-[12.5px] font-medium text-primary" data-testid="ai-composer-context">
              <span className="truncate">{contextLabel}</span>
              {onClearContext ? (
                <button type="button" onClick={(e) => { e.stopPropagation(); onClearContext(); }} className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-primary/15" aria-label="Fjern kontekst" data-testid="ai-context-clear">
                  <X className="h-3 w-3" />
                </button>
              ) : <span className="w-1.5" />}
            </span>
          ) : (
            <span className="truncate text-[12px] text-muted-foreground">{compact ? "Enter sender" : "Enter sender · Shift+Enter for linjeskift"}</span>
          )}
        </div>
        {sending ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={(e) => { e.stopPropagation(); onStop?.(); }} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-[opacity] hover:opacity-90" aria-label="Stopp" data-testid="ai-composer-stop-button">
                <Square className="h-3 w-3 fill-current" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Stopp</TooltipContent>
          </Tooltip>
        ) : (
          <button type="button" onClick={(e) => { e.stopPropagation(); submit(); }} disabled={!value.trim()} className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-[background-color,opacity] hover:bg-brand-deep disabled:opacity-30" aria-label="Send" data-testid="ai-composer-send-button">
            <ArrowUp className="h-4 w-4" strokeWidth={2.25} />
          </button>
        )}
      </div>
    </div>
  );
};
